import React, { useEffect, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';

import CustomHeader from './CustomHeader';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';
import { presignedURLGet } from 'modules/FileManagerV2/api/services';
import GenericPDFViewer from 'components/common/viewers/GenericPDFViewer';
import { useFileMutations } from 'modules/FileManagerV2/hooks/api/useFileMutations';

function FileSummarizeModal() {
  const {
    modals,
    summaryData,
    selectedItem,
    setSummaryData,
    summaryVersions,
    handleOpenConfigureSummaryModal,
    caseDetails,
    isCaseFileManager,
    isLeadFileManager,
  } = useFileManagerContext();
  const { showSummarizeModal, setShowSummarizeModal } = modals;
  const { addToast } = useToast();
  const { saveSummary } = useFileMutations();
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [fileWithUrl, setFileWithUrl] = useState(null);
  const [urlError, setUrlError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [allVersionsForDisplay, setAllVersionsForDisplay] = useState([]);

  const getPdfFilePath = (data) => {
    if (!data) return null;
    return data?.s3_path || null;
  };

  const downloadHandler = async () => {
    try {
      if (!fileWithUrl?.url) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'PDF URL not found');
        return;
      }

      const fileName = selectedItem?.file_name;
      const response = await fetch(fileWithUrl.url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Summary downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to download Summary');
    }
  };

  const regenerateHandler = () => {
    setShowSummarizeModal(false);
    handleOpenConfigureSummaryModal(selectedItem);
  };

  const saveHandler = async () => {
    try {
      setIsSaving(true);
      let caseId;
      if (isCaseFileManager || isLeadFileManager) {
        caseId = caseDetails?.case_id;
      } else {
        caseId = selectedItem?.case_id || caseDetails?.case_id;
      }

      const saveData = {
        case_id: caseId || 'undefined',
        file_name: selectedItem?.file_name,
        summary_s3_file_path: selectedVersion?.s3_path || summaryData?.s3_path,
        summary_version_id: selectedVersion?.version_id || summaryData?.version_id,
        summary_timestamp: selectedVersion?.timestamp || summaryData?.timestamp,
      };

      await saveSummary(saveData);
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Summary saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to save summary');
    } finally {
      setIsSaving(false);
    }
  };

  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
    // setShowVersionHistory(false);
  };

  useEffect(() => {
    if (!showSummarizeModal) {
      setSelectedVersion(null);
      setFileWithUrl(null);
      setUrlError(null);
      setShowVersionHistory(false);
      setAllVersionsForDisplay([]);
    }
  }, [showSummarizeModal]);

  useEffect(() => {
    if (showSummarizeModal && summaryData) {
      const versions = [];
      if (summaryData) {
        versions.push(summaryData);
      }
      if (summaryVersions && Array.isArray(summaryVersions)) {
        versions.push(...summaryVersions);
      }
      setAllVersionsForDisplay(versions);
    }
  }, [showSummarizeModal, summaryData, summaryVersions]);

  useEffect(() => {
    if (summaryData && showSummarizeModal) {
      if (!selectedVersion || selectedVersion?.version_id !== summaryData?.version_id) {
        setSelectedVersion(summaryData);
      }
    }
  }, [summaryData, showSummarizeModal]);

  useEffect(() => {
    const getPresignedUrl = async () => {
      setUrlError(null);

      if (!showSummarizeModal) {
        setFileWithUrl(null);
        setIsLoadingUrl(false);
        return;
      }

      const currentData = selectedVersion || summaryData;
      const filePath = getPdfFilePath(currentData);

      if (!filePath) {
        setFileWithUrl(null);
        setIsLoadingUrl(false);
        return;
      }

      setIsLoadingUrl(true);

      try {
        const response = await presignedURLGet(filePath, currentData?.version_id || null, false, true);

        const summaryItem = {
          file_path: filePath,
          version_id: currentData?.version_id || null,
          url: response.data?.file_url,
          display_name: selectedItem?.file_name,
          file_name: selectedItem?.file_name,
          file_type: 'application/pdf',
        };

        setFileWithUrl(summaryItem);
      } catch (error) {
        console.error('Error getting presigned URL:', error);
        setUrlError('Failed to load PDF. Please try again.');
        setFileWithUrl(null);
      } finally {
        setIsLoadingUrl(false);
      }
    };

    getPresignedUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersion, summaryData, showSummarizeModal, selectedItem]);

  const currentData = selectedVersion || summaryData;
  const hasPdfUrl = Boolean(fileWithUrl?.url);
  const isProcessing = summaryData?.summary_generation_status === 'in_progress';

  const viewerProps = {
    fileUrl: fileWithUrl?.url || fileWithUrl?.file_path,
    fileName: fileWithUrl?.display_name || fileWithUrl?.file_name,
  };

  const handleCloseModal = () => {
    setShowSummarizeModal(false);
    setSummaryData(null);
    setSelectedVersion(null);
    setShowVersionHistory(false);
  };

  const Loader = ({ text }) => (
    <div className="gap-2 justify-content-center d-flex align-items-center" style={{ minHeight: '300px' }}>
      <ProgressSpinner className="m-0 me-2" style={{ width: '20px', height: '20px' }} />
      <span>{text}</span>
    </div>
  );

  const ErrorMessage = ({ text, isError }) => (
    <div className="gap-2 justify-content-center d-flex align-items-center" style={{ minHeight: '300px' }}>
      <span className={`${isError ? 'text-danger' : ''}`}>{text}</span>
    </div>
  );

  const customFooter = () => (
    <div className="footer-section-version">
      {/* FOOTER BUTTONS */}
      <div className="footer-btn">
        {allVersionsForDisplay && allVersionsForDisplay?.length > 1 && (
          <div className="version-toggle">
            <div
              className="d-flex align-items-center cursor-pointer my-2"
              onClick={() => setShowVersionHistory(!showVersionHistory)}
              style={{ userSelect: 'none' }}
            >
              <span className="p-medium text-nowrap">Versions ({allVersionsForDisplay?.length})</span>
              <i className={`pi ${showVersionHistory ? 'pi-chevron-up' : 'pi-chevron-down'} ms-2`}></i>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center w-100">
          <div></div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              label="Regenerate"
              onClick={regenerateHandler}
              disabled={isProcessing}
              className="p-button p-button-secondary outline"
              icon="pi pi-refresh"
            />
            <Button
              label="Download"
              onClick={downloadHandler}
              disabled={isProcessing || summaryData?.isError || !hasPdfUrl}
              className="p-button p-button-secondary outline"
              icon="pi pi-download"
            />
            <Button
              label="Save"
              onClick={saveHandler}
              disabled={isProcessing || summaryData?.isError || !hasPdfUrl || isSaving}
              className="p-button p-button-secondary"
              icon="pi pi-save"
              loading={isSaving}
            />
            <Button label="Close" onClick={handleCloseModal} className="p-button p-button-secondary outline" />
          </div>
        </div>
      </div>

      {/* VERSION LIST – EXPANDS UPWARDS */}
      <div className={`version-container-wrapper ${showVersionHistory ? 'expanded' : ''}`}>
        {showVersionHistory && allVersionsForDisplay && allVersionsForDisplay.length > 1 && (
          <div className="version-container mb-2 mt-0">
            <div className="py-2">
              <div className="d-flex flex-column gap-2">
                {allVersionsForDisplay?.map((version, index) => {
                  const { version_id, created_at, timestamp, parameters, format } = version;

                  const hanleSelectVersion = (e) => {
                    if (selectedVersion?.version_id !== version_id) {
                      e.stopPropagation();
                      handleVersionSelect(version);
                    }
                  };

                  return (
                    <div
                      key={version_id || index}
                      className={`version-item d-flex justify-content-between align-items-center ${
                        selectedVersion?.version_id === version_id ? 'selected-version' : 'unselected-version'
                      }`}
                    >
                      <div className="d-flex flex-column align-items-start">
                        <span className="black-800">
                          {format === 'narrative'
                            ? 'Detailed Narrative'
                            : format === 'bullets'
                            ? 'High Points Bullets'
                            : `Version ${allVersionsForDisplay.length - index}`}
                        </span>

                        <div className="d-flex gap-2 align-items-center" style={{ fontSize: '12px', color: '#6c757d' }}>
                          {created_at && <span>{moment(created_at).format('MM/DD/YYYY, HH:mm')}</span>}
                          {timestamp && <span>{moment(timestamp).format('MM/DD/YYYY, HH:mm')}</span>}
                          {parameters?.summary_percentage && <span>{parameters?.summary_percentage}% of original</span>}{' '}
                        </div>
                      </div>

                      <Button
                        label={selectedVersion?.version_id === version_id ? 'Selected' : 'View'}
                        onClick={hanleSelectVersion}
                        className={`p-button p-button-text p-button-sm${
                          selectedVersion?.version_id === version_id ? ' pe-none selected-btn' : ''
                        }`}
                        style={{ color: 'var(--primary-middle)' }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      className="new-task-popup"
      header={<CustomHeader onHide={handleCloseModal} />}
      visible={showSummarizeModal}
      modal
      onHide={handleCloseModal}
      closable={false}
      style={{ width: '90vw', maxWidth: '900px' }}
      contentStyle={{ maxHeight: '85vh', overflow: 'auto' }}
      footer={customFooter}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="modal-heading F-size16 text-bold">{`Summary of ${selectedItem?.file_name}`}</div>
      </div>

      <div className="d-flex mt-2 align-items-center mb-3">
        <span className="d-flex p-0 black-700" style={{ lineHeight: '18px', fontSize: '12px' }}>
          <i className="pi pi-info-circle me-2"></i>
          AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent verification.
        </span>
      </div>

      <div className="summary-file-viewer" style={{ width: '100%' }}>
        {isProcessing ? (
          <Loader text="Summary is being generated" />
        ) : urlError ? (
          <ErrorMessage text={urlError} isError={true} />
        ) : isLoadingUrl || (currentData?.s3_path && !hasPdfUrl && !urlError) ? (
          <Loader text="Loading summary..." />
        ) : hasPdfUrl && fileWithUrl ? (
          <div style={{ width: '100%', overflow: 'hidden' }}>
            <GenericPDFViewer {...viewerProps} />
          </div>
        ) : summaryData?.isError ? (
          <ErrorMessage text="Error generating summary" isError={true} />
        ) : (
          <ErrorMessage text="No summary available" isError={false} />
        )}
      </div>
    </Dialog>
  );
}

export default FileSummarizeModal;
